import { fetchEpisodesRequest, fetchLearningPathsRequest } from '@/requests';
import { AnalyticsService, EVENTS } from '@/services';
import { ComplianceType, EpisodeCard, LearningPath, Referrer } from '@/types';
import { searchClient, SearchIndices } from '@/utils';

import { transformEpisodeCard } from '../episodes-api/episodes-api.utils';
import { transformLearningPath } from '../learning-paths/learning-paths.utils';
import { postgrestApi } from '../postgrest-api';
import { PostgrestApiTags } from '../store.constants';
import { rtkQueryError } from '../store.utils';

type FetchCatalogSearchResultsParams = {
  searchTerm: string;
  compliance: ComplianceType;
  organizationName?: string;
  audienceRegions?: string[];
  referrer?: Referrer;
};

export const searchApi = postgrestApi.injectEndpoints({
  endpoints: builder => ({
    fetchQuerySuggestions: builder.query<string[], { searchTerm: string }>({
      queryFn: async ({ searchTerm }) => {
        try {
          const episodeQuerySuggestionsQuery = {
            indexName: SearchIndices.EPISODES_QUERY_SUGGESTIONS,
            query: searchTerm,
            params: { hitsPerPage: 8 },
          };
          const { results } = await searchClient.searchForHits([
            episodeQuerySuggestionsQuery,
          ]);
          return { data: results[0].hits.map((hit: any) => hit.query) };
        } catch (error) {
          return rtkQueryError(error);
        }
      },
    }),
    fetchCatalogSearchResults: builder.query<
      { episodes: EpisodeCard[]; learningPaths: LearningPath[] },
      FetchCatalogSearchResultsParams
    >({
      providesTags: [PostgrestApiTags.SEARCH_RESULTS],
      onQueryStarted: async ({ searchTerm, referrer }, { queryFulfilled }) => {
        const searchProps = {
          searchTerm,
          location: referrer || '',
        };
        try {
          AnalyticsService.searchSetFilter({
            searchTerm,
            referrer,
          });
          AnalyticsService.logEvent(EVENTS.GA4_KEY_EVENTS.SEARCH, {
            search_term: searchTerm,
          });

          AnalyticsService.logEvent(
            EVENTS.SEARCH_EVENTS.SUBMIT.INTENT,
            searchProps,
          );
          await queryFulfilled;
          AnalyticsService.logEvent(
            EVENTS.SEARCH_EVENTS.SUBMIT.SUCCESS,
            searchProps,
          );
        } catch (error: any) {
          AnalyticsService.logEvent(EVENTS.SEARCH_EVENTS.SUBMIT.FAIL, {
            ...searchProps,
            error: error?.message,
          });
        }
      },
      queryFn: async (
        { searchTerm, compliance, organizationName, audienceRegions },
        redux,
        extraOptions,
        baseQuery,
      ) => {
        try {
          const { results } = await searchClient.searchForHits([
            {
              indexName: SearchIndices.EPISODES,
              params: {
                query: searchTerm,
                attributesToRetrieve: [],
                attributesToHighlight: [],
                hitsPerPage: 100,
                facetFilters: ['is_archived:false'],
                optionalFilters: [
                  ...(audienceRegions
                    ? [audienceRegions.map(r => `audience_regions:${r}`)]
                    : []),
                ],
              },
            },
            {
              indexName: SearchIndices.LEARNING_PATHS,
              params: {
                query: searchTerm,
                attributesToRetrieve: [],
                attributesToHighlight: [],
                hitsPerPage: 5,
                filters: 'is_archived=0',
                facetFilters: [
                  [
                    'organization_name:LumiQ',
                    ...(organizationName
                      ? [`organization_name:${organizationName}`]
                      : []),
                  ],
                ],
              },
            },
          ]);

          const episodeHitIds = results[0].hits.map(hit => hit.objectID);
          const learningPathHits = results[1].hits.map(hit => +hit.objectID);

          const [episodeFetchResult, learningPathFetchResult] =
            await Promise.all([
              baseQuery(
                fetchEpisodesRequest({
                  episodeIds: episodeHitIds,
                  compliance,
                }),
              ),
              baseQuery(
                fetchLearningPathsRequest({
                  learningPathIds: learningPathHits,
                  compliance,
                }),
              ),
            ]);
          if (episodeFetchResult.error)
            return { error: episodeFetchResult.error };
          if (learningPathFetchResult.error)
            return { error: learningPathFetchResult.error };

          return {
            data: {
              episodes: (episodeFetchResult.data as unknown[])
                .map(e => transformEpisodeCard(e))
                .sort(
                  (a, b) =>
                    episodeHitIds.indexOf(a.episodeId) -
                    episodeHitIds.indexOf(b.episodeId),
                ),
              learningPaths: (learningPathFetchResult.data as unknown[])
                .map(transformLearningPath)
                .sort(
                  (a, b) =>
                    learningPathHits.indexOf(a.groupId) -
                    learningPathHits.indexOf(b.groupId),
                ),
            },
          };
        } catch (error) {
          return rtkQueryError(error);
        }
      },
    }),
  }),
});

export const {
  useFetchCatalogSearchResultsQuery,
  useLazyFetchQuerySuggestionsQuery,
} = searchApi;
